// @flow

import * as React from 'react';
import { Query } from 'react-apollo';
import { connect } from 'react-redux';

import Window from '@ta/events.window';
import WindowScroll from '@ta/events.window-scroll';
import type { HoverChainNode } from '@ta/common.hover-chain';

import Menu from './Menu';
import MenuList from './MenuList';
import MenuListItem from './MenuListItem';
import { convertAndPushToArray } from '../GlobalNavLinksV2Converter';

import type { Position } from './Menu';
import type { LinkData } from '../GlobalNavLinksV2Converter';
import type { NavLinkType } from '../types';

import GlobalNavLinksV2Query from '../GlobalNavLinksV2Query.gql';

type ExternalProps = {|
  /** Name of the overlay to trigger */
  name: string,

  /** the node that will trigger the menu */
  triggerNode: React.Node,

  /** The node to show if there aren't menu for this geo */
  noMenuNode: React.Node,

  /** open the menu with onclick instead of hover */
  onClickInsteadOfHover: boolean,
|};

type Props = {|
  ...ExternalProps,
  route: {
    page: string,
    geo: string,
    detail: string,
    longitude: string,
    latitude: string,
    isNearby: boolean,
  },
  page: {
    geoId: string,
  },
  i18n: { locale: string },
|};

const convertToNavLinkType = (links: ?(any[])) => {
  const navLinks: LinkData[] = [];
  if (links) {
    const linkMap = {};
    links.forEach(link => {
      linkMap[link.linkId] = link;
    });

    // we need to use the scoped-home menu as the base menu
    const tourismMenu = linkMap['scoped-home'];
    const hotelsMenu = linkMap.hotels;

    if (tourismMenu && tourismMenu.sublinks && hotelsMenu) {
      tourismMenu.sublinks
        .filter(data => data.linkId !== 'tourism')
        .map(link => {
          let subLinks = link.sublinks;
          // use hotels submenu
          if (link.url === hotelsMenu.url) {
            subLinks = hotelsMenu.sublinks;
          }
          return {
            anchorText: link.anchorText,
            url: link.url,
            linkId: link.linkId,
            sublinks: subLinks,
          };
        })
        .forEach(data => {
          navLinks.push(data);
        });
    }
  }
  const navLinkTypes: NavLinkType[] = [];
  convertAndPushToArray(navLinks, false, false, navLinkTypes);
  return navLinkTypes;
};

export const ssrQueries = ({
  i18n,
  route,
  page,
}: {
  i18n: { locale: string },
  route: {
    page: string,
    geo: string,
    detail: string,
    longitude: string,
    latitude: string,
    isNearby: boolean,
  },
  page: {
    geoId: string,
  },
}) => [
  {
    query: GlobalNavLinksV2Query,
    variables: {
      pos: i18n.locale,
      servlet: route.page === 'Home' && route.geo ? 'Tourism' : route.page,
      urlParameters: {
        longitude: route.longitude,
        latitude: route.latitude,
        geo: route.isNearby ? page.geoId : route.geo,
        detail: route.detail,
      },
    },
  },
];

/** Provides a hover menu with access to the full site menu */
class HoverMenu extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    this.menuRef = null;
    this.menuIsOpen = false;
  }

  createMenu = (
    links: ?(any[]),
    parentPtr?: HoverChainNode,
    topLevelPosition?: Position,
    minHeight: number,
    isTopLevel: boolean,
  ) => {
    if (!links) {
      return null;
    }
    return links.map<React.Node>(link => {
      if (!link.subList || !link.subList.length) {
        return <MenuListItem link={link} key={`${link.id}_${link.text}`} />;
      }
      const anchorItem = <MenuListItem link={link} key={`${link.id}_${link.text}`} hasSubItem />;

      // need to adjust the menu to make them line-up better
      let adjustedPositon = topLevelPosition;
      if (isTopLevel && topLevelPosition && topLevelPosition.top) {
        // the sub-menu are too high, so adjust it down 1px.
        const { top } = topLevelPosition;
        adjustedPositon = {
          position: topLevelPosition.position,
          left: topLevelPosition.left,
          right: topLevelPosition.right,
          top: `${parseInt(top.replace('px', ''), 10) + 1}px`,
        };
      }

      return (
        <Menu
          submenu
          key={`${link.id}_${link.text}`}
          triggerActive={false}
          tabType="nonlink"
          nonLinkContents={anchorItem}
          hoverChainParent={parentPtr}
          topLevelPosition={adjustedPositon}
          isAside
        >
          {(ptr, position) => (
            <MenuList minHeight={`${minHeight}px`}>
              {this.createMenu(link.subList, ptr, position, minHeight, false)}
            </MenuList>
          )}
        </Menu>
      );
    });
  };

  menuIsOpen: boolean;

  menuRef: any;

  renderItemText = (
    setTarget: (el: any) => void,
    setWrapper: (el: any) => void,
    open: () => void,
    close: () => void,
  ) => {
    const openMenu = () => {
      if (!this.menuIsOpen) {
        this.menuIsOpen = true;
        open();
      }
    };

    const closeMenu = () => {
      if (this.menuIsOpen) {
        this.menuIsOpen = false;
        close();
      }
    };

    const onClickEvent = () => {
      if (this.props.onClickInsteadOfHover) {
        if (!this.menuIsOpen) {
          openMenu();
        } else {
          closeMenu();
        }
      }
    };
    return (
      <React.Fragment>
        <span
          style={{ display: 'inline-block' }}
          ref={el => {
            if (!this.props.onClickInsteadOfHover) {
              setTarget(el);
            }
            setWrapper(el);
          }}
          onClick={onClickEvent}
        >
          {this.props.triggerNode}
        </span>
        <WindowScroll callback={closeMenu} />
        <Window event="click" callback={closeMenu} />
      </React.Fragment>
    );
  };

  render() {
    const queryProps = {
      i18n: this.props.i18n,
      route: this.props.route,
      page: this.props.page,
    };
    const query = ssrQueries(queryProps);
    return (
      <Query query={query[0].query} variables={query[0].variables}>
        {({ loading, data, error }) => {
          if (loading || error) {
            return this.props.noMenuNode;
          }
          // no data from server
          if (!data || !data.globalNavLinks || !data.globalNavLinks.sublinks) {
            return this.props.noMenuNode;
          }
          const links: NavLinkType[] = convertToNavLinkType(data?.globalNavLinks?.sublinks);
          return (
            <Menu
              triggerActive={false}
              itemText={this.renderItemText}
              menuStateCallback={isOpen => {
                this.menuIsOpen = isOpen;
              }}
            >
              {(ptr, topLevelPosition) => {
                const childElement = this.createMenu(
                  links,
                  ptr,
                  topLevelPosition,
                  this.menuRef?.clientHeight - 1 || 0,
                  true,
                );
                return (
                  <MenuList
                    setRef={el => {
                      this.menuRef = el;
                    }}
                  >
                    {childElement}
                  </MenuList>
                );
              }}
            </Menu>
          );
        }}
      </Query>
    );
  }
}

export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  route: state.route,
  page: state.page,
  i18n: state.i18n,
}))(HoverMenu);
